import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoutModal from "components/LogoutModal";
import ChangeCompanyModal from "components/ChangeCompanyModal";
import AddCompanyModal from "components/AddCompanyModal";
import "./UserMenuPortalPopover.sass";

const getUserInitials = (first_name, last_name) => {
  const first = (first_name ?? '').trim();
  const last = (last_name ?? '').trim();
  
  if (!first && !last) {
    return '';
  }
  
  const firstLetter = first?.at(0)?.toUpperCase() ?? '';
  const lastLetter = last?.at(0)?.toUpperCase() ?? '';
  
  if (firstLetter && lastLetter) {
    return `${firstLetter}${lastLetter}`;
  }
  
  if (firstLetter) {
    return `${firstLetter}${firstLetter}`;
  }
  
  if (lastLetter) {
    return `${lastLetter}${lastLetter}`;
  }
  
  return '';
};

const buildUserName = (first_name, last_name, email) => {
  const name = [first_name, last_name].filter(Boolean).join(" ").trim();
  if (name) return name;
  return email || "User";
};

const getDefaultContainer = () =>
  document.querySelector(".Polaris-Frame") ||
  document.querySelector(".Polaris-Page") ||
  document.body;

export default function UserMenuPortalPopover({
  containerSelector,
  onPersonalSettings,
  onSupport,
  onLogout,
  onChangeCompany,
  avatarColor = "#E4F3FE",
  initials: initialsProp,
  userName: userNameProp,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user?.user) || {};
  const { email, first_name, last_name } = user;

  const photoUrl = useMemo(() => {
    return user?.photo || user?.picture || null;
  }, [user?.photo, user?.picture]);

  const initials = useMemo(() => {
    return initialsProp ?? getUserInitials(first_name, last_name);
  }, [initialsProp, first_name, last_name]);

  const userName = useMemo(() => {
    return userNameProp ?? buildUserName(first_name, last_name, email);
  }, [userNameProp, first_name, last_name, email]);

  const btnRef = useRef(null);
  const popRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [changeCompanyOpen, setChangeCompanyOpen] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState("bottom");

  const getContainer = () => {
    if (typeof containerSelector === "function") return containerSelector();
    if (typeof containerSelector === "string")
      return document.querySelector(containerSelector) || getDefaultContainer();
    return getDefaultContainer();
  };

  const measureAndPlace = () => {
    if (!btnRef.current) return;

    const rect = btnRef.current.getBoundingClientRect();

    const DROPDOWN_W = 240;
    const HEADER_H = 64;
    const LIST_H = 160;
    const DROPDOWN_H = HEADER_H + LIST_H;
    const GAP = 4;
    const SAFE = 8;
    const RIGHT_OFFSET = 30;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom - SAFE;
    const spaceAbove = rect.top - SAFE;

    const nextPlacement =
      spaceBelow < DROPDOWN_H && spaceAbove > spaceBelow ? "top" : "bottom";
    setPlacement(nextPlacement);

    let left = vw - DROPDOWN_W - RIGHT_OFFSET;
    if (left < SAFE) left = SAFE;

    let top =
      nextPlacement === "bottom"
        ? rect.bottom + GAP
        : rect.top - DROPDOWN_H - GAP;

    if (top < SAFE) top = SAFE;
    if (top + DROPDOWN_H > vh - SAFE) top = vh - DROPDOWN_H - SAFE;

    setCoords({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    measureAndPlace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    const handleResize = () => setOpen(false);
    const handleScroll = () => setOpen(false);

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const handleSupportClick = () => {
    setOpen(false);
    if (onSupport) {
      onSupport();
    } else {
      navigate("/help");
    }
  };

  const handleChangeCompanyClick = () => {
    setOpen(false);
    if (onChangeCompany) {
      onChangeCompany();
      return;
    }
    setChangeCompanyOpen(true);
  };

  const handleAddCompanyClick = () => {
    setChangeCompanyOpen(false);
    setAddCompanyOpen(true);
  };

  const handleAddCompanyClose = () => {
    setAddCompanyOpen(false);
    setChangeCompanyOpen(true);
  };

  const handleLogoutClick = () => {
    setOpen(false);
    if (onLogout) {
      onLogout();
      return;
    }
    setLogoutModalOpen(true);
  };

  const run = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  const container = getContainer();

  return (
    <>
      <LogoutModal
        visible={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
      />
      <ChangeCompanyModal
        visible={changeCompanyOpen}
        onClose={() => setChangeCompanyOpen(false)}
        onAddCompanyClick={handleAddCompanyClick}
      />
      <AddCompanyModal
        visible={addCompanyOpen}
        onClose={handleAddCompanyClose}
      />
      <div
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="UserMenuActivator"
        role="button"
        tabIndex={0}
        title={userName}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((v) => !v);
          if (e.key === "Escape") setOpen(false);
        }}
      >
        <div
          className="topbar-wrapper_block-avatar"
          style={{
            backgroundColor: photoUrl ? "transparent" : avatarColor,
            color: photoUrl ? "transparent" : "rgba(38, 127, 215, 1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: 600,
            borderRadius: "50%",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            overflow: "hidden",
            position: "relative",
          }}
          title={userName}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={userName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : (
            initials
          )}
        </div>
      </div>

      {open &&
        container &&
        createPortal(
          <div
            ref={popRef}
            className={`UserMenuPopoverPortal UserMenuPopoverPortal--${placement}`}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 9999,
            }}
          >
            <div className="UserMenuPopover" role="menu">
              <div className="UserMenuPopover__header">
                <div className="UserMenuPopover__avatarWrap" aria-hidden>
                  <div
                    className="UserMenuAvatar"
                    style={{
                      backgroundColor: photoUrl ? "transparent" : avatarColor,
                      overflow: "hidden",
                      position: "relative",
                    }}
                    title={userName}
                  >
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={userName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      initials
                    )}
                  </div>
                </div>

                <div className="UserMenuPopover__who">
                  <div className="UserMenuPopover__name" title={userName}>
                    {userName}
                  </div>

                  {!!email && (
                    <div className="UserMenuPopover__email" title={email}>
                      {email}
                    </div>
                  )}
                </div>
              </div>

              <div className="UserMenuPopover__list">
                <button
                  className="UserMenuPopover__item"
                  type="button"
                  onClick={run(onPersonalSettings)}
                  role="menuitem"
                >
                  {t('userMenu.personalSettings')}
                </button>

                <button
                  className="UserMenuPopover__item"
                  type="button"
                  onClick={handleChangeCompanyClick}
                  role="menuitem"
                >
                  {t('userMenu.changeCompany')}
                </button>

                <button
                  className="UserMenuPopover__item"
                  type="button"
                  onClick={handleSupportClick}
                  role="menuitem"
                >
                  {t('userMenu.support')}
                </button>

                <button
                  className="UserMenuPopover__item UserMenuPopover__item--danger"
                  type="button"
                  onClick={handleLogoutClick}
                  role="menuitem"
                >
                  {t('userMenu.logOut')}
                </button>
              </div>
            </div>
          </div>,
          container
        )}
    </>
  );
}
